import { DeductionListJson } from "@/api"
import { DateKind } from "@/global_filter"
import { TableState } from "@/deduction_table/table_state"
import { Option, currencyFormatter } from "@/utils"
import { aging_row_amount } from "@/utils/deduction_age"
import { format_deduction_status_label } from "@/utils/deduction_status_labels"
import { notifyChartFilterApplied } from "@/utils/filter_feedback"
import { STATUS_COLORS, SORT_ORDER } from "@/deduction/StatusBreakdown.tsx"
import { format, parseISO } from "date-fns"
import { useMemo } from "preact/compat"

/**
 * The date that decides which month a row belongs to, mirroring the server's
 * `effective_date` in `server/src/deduction/list_handler.rs` (check/posting fall
 * back to invoice_date when unset).
 */
export function effective_date_for_kind(
  row: Pick<DeductionListJson, "invoice_date" | "check_date" | "posting_date">,
  dateKind: DateKind
): string {
  switch (dateKind) {
    case "check":
      return row.check_date || row.invoice_date
    case "posting":
      return row.posting_date || row.invoice_date
    default:
      return row.invoice_date
  }
}

/** "2026-06-15" -> "2026-06". Works on any ISO date/datetime string. */
export function month_key(iso_date: string): string {
  return iso_date.slice(0, 7)
}

/** Every "yyyy-MM" key from the month containing `start` through the month containing `end`. */
export function month_keys_between(start: Date, end: Date): string[] {
  const keys: string[] = []
  let year = start.getFullYear()
  let month = start.getMonth()
  const end_year = end.getFullYear()
  const end_month = end.getMonth()
  while (year < end_year || (year === end_year && month <= end_month)) {
    keys.push(`${year}-${String(month + 1).padStart(2, "0")}`)
    month += 1
    if (month === 12) {
      month = 0
      year += 1
    }
  }
  return keys
}

export interface MonthlyStageRow {
  key: string
  color: string
  total_count: number
  total_amount: number
  /** Signed $ per month key; only keys with rows are present. */
  by_month: Record<string, number>
}

export interface MonthlyStageBreakdown {
  /** Ordered month keys spanning the filter range plus any stragglers found in the data. */
  months: string[]
  /** One row per stage present in the data, in SORT_ORDER. */
  rows: MonthlyStageRow[]
  month_totals: Record<string, number>
  grand_total: number
}

/**
 * Buckets the already-filtered deduction list into stage x month cells using the
 * same per-row amount as the stage chart (`aging_row_amount`), so the two views
 * of this card always agree on totals.
 */
export function tally_by_month(
  data: DeductionListJson[],
  dateKind: DateKind,
  startDate: Option<Date>,
  endDate: Option<Date>
): MonthlyStageBreakdown {
  const range_months = startDate && endDate && startDate <= endDate ? month_keys_between(startDate, endDate) : []
  const month_set = new Set(range_months)
  const stages = new Map<string, MonthlyStageRow>()
  const month_totals: Record<string, number> = {}
  let grand_total = 0

  for (const row of data) {
    const month = month_key(effective_date_for_kind(row, dateKind))
    month_set.add(month)
    let stage = stages.get(row.status_value)
    if (!stage) {
      stage = {
        key: row.status_value,
        color: STATUS_COLORS[row.status_value] || "gray",
        total_count: 0,
        total_amount: 0,
        by_month: {},
      }
      stages.set(row.status_value, stage)
    }
    const amount = aging_row_amount(row)
    stage.total_count += 1
    stage.total_amount += amount
    stage.by_month[month] = (stage.by_month[month] || 0) + amount
    month_totals[month] = (month_totals[month] || 0) + amount
    grand_total += amount
  }

  const months = [...month_set].sort()
  const rows = [...stages.values()].sort(
    (a, b) =>
      (SORT_ORDER[a.key as keyof typeof SORT_ORDER] ?? 99) - (SORT_ORDER[b.key as keyof typeof SORT_ORDER] ?? 99)
  )
  return { months, rows, month_totals, grand_total }
}

// Tailwind's JIT only picks up class names written out literally, so map the
// STATUS_COLORS base colors to static bg-* classes instead of interpolating.
const DOT_CLASSES: Record<string, string> = {
  "red-500": "bg-red-500",
  "amber-500": "bg-amber-500",
  "yellow-500": "bg-yellow-500",
  "green-500": "bg-green-500",
  "slate-500 text-white": "bg-slate-500",
}

/** "2026-06" -> "Jun '26" for column headers. */
export function format_month_label(key: string): string {
  return format(parseISO(`${key}-01`), "MMM ''yy")
}

function format_cell(amount: number | undefined): string {
  if (!amount) return "–"
  return currencyFormatter(Math.abs(amount))
}

interface StatusBreakdownByMonthProps {
  data: DeductionListJson[]
  dateKind: DateKind
  startDate: Option<Date>
  endDate: Option<Date>
}

export function StatusBreakdownByMonth({ data, dateKind, startDate, endDate }: StatusBreakdownByMonthProps) {
  const breakdown = useMemo(
    () => tally_by_month(data || [], dateKind, startDate, endDate),
    [data, dateKind, startDate, endDate]
  )

  function filter_stage(stage_key: string) {
    TableState.set(s => ({
      ...s,
      search: "",
      earlyPay: false,
      columnFilters: [{ id: "status_value", value: stage_key }],
      pagination: { pageIndex: 0, pageSize: s.pagination.pageSize },
    }))
    notifyChartFilterApplied(`Showing deductions in stage "${format_deduction_status_label(stage_key)}".`)
  }

  if (breakdown.rows.length === 0) {
    return <div class="text-sm text-slate-500">No deductions in the selected period.</div>
  }

  return (
    <div class="overflow-x-auto">
      <table class="w-full text-xs">
        <thead>
          <tr class="border-b text-slate-500">
            <th class="sticky left-0 z-10 bg-card py-1 pr-2 text-left font-medium">Stage</th>
            {breakdown.months.map(m => (
              <th key={m} class="py-1 px-2 text-right font-medium whitespace-nowrap">
                {format_month_label(m)}
              </th>
            ))}
            <th class="py-1 pl-2 text-right font-medium">Total</th>
          </tr>
        </thead>
        <tbody>
          {breakdown.rows.map(row => (
            <tr key={row.key} class="border-b border-slate-100 hover:bg-slate-50">
              <td class="sticky left-0 z-10 bg-card py-1 pr-2 whitespace-nowrap">
                <button
                  type="button"
                  class="flex cursor-pointer items-center gap-1.5 text-left hover:underline"
                  onClick={() => filter_stage(row.key)}
                >
                  <span class={`inline-block h-2 w-2 shrink-0 rounded-full ${DOT_CLASSES[row.color] || "bg-gray-400"}`} />
                  {format_deduction_status_label(row.key)} ({row.total_count})
                </button>
              </td>
              {breakdown.months.map(m => (
                <td key={m} class="py-1 px-2 text-right tabular-nums whitespace-nowrap">
                  {format_cell(row.by_month[m])}
                </td>
              ))}
              <td class="py-1 pl-2 text-right font-medium tabular-nums whitespace-nowrap">
                {format_cell(row.total_amount)}
              </td>
            </tr>
          ))}
          <tr class="font-medium">
            <td class="sticky left-0 z-10 bg-card py-1 pr-2">Grand Total</td>
            {breakdown.months.map(m => (
              <td key={m} class="py-1 px-2 text-right tabular-nums whitespace-nowrap">
                {format_cell(breakdown.month_totals[m])}
              </td>
            ))}
            <td class="py-1 pl-2 text-right tabular-nums whitespace-nowrap">{format_cell(breakdown.grand_total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

if (import.meta.vitest) {
  const { describe, it, expect } = import.meta.vitest

  function row(overrides: Partial<DeductionListJson>): DeductionListJson {
    return {
      kind: "deduction",
      invoice_date: "2026-06-15",
      status_value: "new",
      customer_name: "KeHE",
      deduction_amount: 100,
      ...overrides,
    } as DeductionListJson
  }

  describe("effective_date_for_kind", () => {
    const r = row({ invoice_date: "2026-06-01", check_date: "2026-07-02", posting_date: "2026-08-03" })

    it("picks the date column for the selected kind", () => {
      expect(effective_date_for_kind(r, "invoice")).toBe("2026-06-01")
      expect(effective_date_for_kind(r, "check")).toBe("2026-07-02")
      expect(effective_date_for_kind(r, "posting")).toBe("2026-08-03")
    })

    it("falls back to invoice_date when check/posting are missing, matching the server", () => {
      const bare = row({ invoice_date: "2026-06-01", check_date: undefined, posting_date: null })
      expect(effective_date_for_kind(bare, "check")).toBe("2026-06-01")
      expect(effective_date_for_kind(bare, "posting")).toBe("2026-06-01")
    })
  })

  describe("month_keys_between", () => {
    it("spans months inclusive of both endpoints", () => {
      expect(month_keys_between(parseISO("2026-06-05"), parseISO("2026-08-20"))).toEqual([
        "2026-06",
        "2026-07",
        "2026-08",
      ])
    })

    it("crosses year boundaries", () => {
      expect(month_keys_between(parseISO("2025-11-30"), parseISO("2026-01-01"))).toEqual([
        "2025-11",
        "2025-12",
        "2026-01",
      ])
    })

    it("returns a single month when start and end share one", () => {
      expect(month_keys_between(parseISO("2026-06-01"), parseISO("2026-06-30"))).toEqual(["2026-06"])
    })
  })

  describe("tally_by_month", () => {
    const data = [
      row({ status_value: "new", invoice_date: "2026-06-10", deduction_amount: 100 }),
      row({ status_value: "new", invoice_date: "2026-07-10", deduction_amount: 50 }),
      row({ status_value: "validated", invoice_date: "2026-07-01", deduction_amount: 200 }),
      row({ status_value: "won", invoice_date: "2026-06-20", deduction_amount: 10, won_amount: 25 }),
    ]

    it("buckets amounts by stage and month using the filter date kind", () => {
      const b = tally_by_month(data, "invoice", parseISO("2026-06-01"), parseISO("2026-08-31"))
      expect(b.months).toEqual(["2026-06", "2026-07", "2026-08"])
      const new_row = b.rows.find(r => r.key === "new")!
      expect(new_row.by_month).toEqual({ "2026-06": 100, "2026-07": 50 })
      expect(new_row.total_amount).toBe(150)
      expect(new_row.total_count).toBe(2)
      expect(b.month_totals["2026-06"]).toBe(125)
      expect(b.month_totals["2026-07"]).toBe(250)
      expect(b.grand_total).toBe(375)
    })

    it("uses aging_row_amount rules (won rows count won_amount)", () => {
      const b = tally_by_month(data, "invoice", null, null)
      const won = b.rows.find(r => r.key === "won")!
      expect(won.total_amount).toBe(25)
    })

    it("orders stages by the shared SORT_ORDER", () => {
      const b = tally_by_month(data, "invoice", null, null)
      expect(b.rows.map(r => r.key)).toEqual(["new", "validated", "won"])
    })

    it("check date kind buckets by check_date with invoice fallback", () => {
      const mixed = [
        row({ status_value: "new", invoice_date: "2026-06-10", check_date: "2026-07-05", deduction_amount: 100 }),
        row({ status_value: "new", invoice_date: "2026-06-10", check_date: undefined, deduction_amount: 40 }),
      ]
      const b = tally_by_month(mixed, "check", null, null)
      const new_row = b.rows.find(r => r.key === "new")!
      expect(new_row.by_month).toEqual({ "2026-07": 100, "2026-06": 40 })
    })

    it("includes months outside the range when data falls there, and empty range months", () => {
      const b = tally_by_month(
        [row({ invoice_date: "2026-03-15" })],
        "invoice",
        parseISO("2026-06-01"),
        parseISO("2026-07-31")
      )
      expect(b.months).toEqual(["2026-03", "2026-06", "2026-07"])
    })

    it("derives months from data alone when no range is set", () => {
      const b = tally_by_month(data, "invoice", null, null)
      expect(b.months).toEqual(["2026-06", "2026-07"])
    })
  })

  describe("format_month_label", () => {
    it("renders compact month labels", () => {
      expect(format_month_label("2026-06")).toBe("Jun '26")
    })
  })
}
